// Profile page · /#/profile
// Self-serve: edit own name + email, change own password. Available to every
// authenticated user. onUpdated lifts the refreshed staff record into App so
// the sidebar reflects a name change immediately.

import { useRef, useState } from 'react';
import { updateProfile, changePassword, uploadProfilePic } from '../lib/api.ts';
import { Avatar } from '../components/Avatar.jsx';

const ROLE_LABEL = { admin: '管理员', store_manager: '店长', staff: '员工' };

export const Profile = ({ user, onUpdated }) => {
  return (
    <>
      <div className="vava-page-header">
        <div>
          <h1 className="vava-page-title">个人资料</h1>
          <div className="vava-page-meta">
            {user.title || '—'} · {ROLE_LABEL[user.role] || user.role}
          </div>
        </div>
      </div>
      <div style={{ maxWidth: 520, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <AvatarCard user={user} onUpdated={onUpdated} />
        <ProfileCard user={user} onUpdated={onUpdated} />
        <PasswordCard />
      </div>
    </>
  );
};

const AvatarCard = ({ user, onUpdated }) => {
  const fileRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);          // { kind, text }

  async function pick() {
    fileRef.current?.click();
  }

  async function onFile(e) {
    const f = e.target.files?.[0];
    e.target.value = '';                          // reset so re-selecting the same file fires onChange
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      setMsg({ kind: 'error', text: '只接受图片文件' });
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      setMsg({ kind: 'error', text: '图片太大，请选小于 2MB 的文件' });
      return;
    }
    setMsg(null);
    setSaving(true);
    try {
      const updated = await uploadProfilePic(f);
      onUpdated(updated);
      setMsg({ kind: 'success', text: '头像已更新' });
    } catch (e) {
      setMsg({ kind: 'error', text: e?.message || '上传失败' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="vava-card" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <Avatar staff={user} large />
      <div style={{ flex: 1, minWidth: 0 }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 4px' }}>头像</h2>
        <div style={{ fontSize: 13, color: 'var(--vava-text-muted)' }}>
          上传一张图片（最大 2MB）。会出现在左下角和团队列表里。
        </div>
        <button type="button" className="btn btn-secondary"
                style={{ marginTop: 12 }}
                disabled={saving} onClick={pick}>
          {saving ? '上传中…' : (user.profilePicKey ? '更换头像' : '上传头像')}
        </button>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} />
        <Notice kind={msg?.kind}>{msg?.text}</Notice>
      </div>
    </div>
  );
};

const Notice = ({ kind, children }) => {
  if (!children) return null;
  const ok = kind === 'success';
  return (
    <div role={ok ? 'status' : 'alert'} style={{
      marginTop: 12, padding: '8px 12px', borderRadius: 8, fontSize: 13,
      background: ok ? 'var(--vava-green-bg)' : 'var(--vava-red-bg)',
      color: ok ? 'var(--vava-green)' : 'var(--vava-red)'
    }}>{children}</div>
  );
};

const ProfileCard = ({ user, onUpdated }) => {
  const [name, setName] = useState(user.name || '');
  const [email, setEmail] = useState(user.email || '');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);          // { kind, text }

  const dirty = name.trim() !== (user.name || '') || email.trim() !== (user.email || '');

  async function save() {
    setMsg(null);
    if (!name.trim()) { setMsg({ kind: 'error', text: '姓名不能为空' }); return; }
    setSaving(true);
    try {
      const staff = await updateProfile({
        name: name.trim(),
        email: email.trim() || null
      });
      onUpdated(staff);
      setMsg({ kind: 'success', text: '已保存' });
    } catch (e) {
      setMsg({ kind: 'error', text: e?.message || '保存失败' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="vava-card">
      <h2 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 16px' }}>基本信息</h2>
      <div className="vava-field">
        <label className="vava-label">姓名</label>
        <input className="vava-input" value={name}
               onChange={(e) => setName(e.target.value)} disabled={saving} />
      </div>
      <div className="vava-field">
        <label className="vava-label">邮箱</label>
        <input className="vava-input" type="email" value={email}
               placeholder="用于接收通知 / 邀请邮件"
               onChange={(e) => setEmail(e.target.value)} disabled={saving} />
      </div>
      <div style={{ marginTop: 4 }}>
        <button className="btn btn-primary" onClick={save} disabled={saving || !dirty}>
          {saving ? '保存中…' : '保存'}
        </button>
      </div>
      <Notice kind={msg?.kind}>{msg?.text}</Notice>
    </div>
  );
};

const PasswordCard = () => {
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  async function save() {
    setMsg(null);
    if (!cur || !next) { setMsg({ kind: 'error', text: '请填写当前密码和新密码' }); return; }
    if (next.length < 8) { setMsg({ kind: 'error', text: '新密码至少 8 位' }); return; }
    if (next !== confirm) { setMsg({ kind: 'error', text: '两次输入的新密码不一致' }); return; }
    setSaving(true);
    try {
      await changePassword(cur, next);
      setCur(''); setNext(''); setConfirm('');
      setMsg({ kind: 'success', text: '密码已修改 · 其他设备的登录已退出' });
    } catch (e) {
      const text = e?.status === 403 ? '当前密码不正确' : (e?.message || '修改失败');
      setMsg({ kind: 'error', text });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="vava-card">
      <h2 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 16px' }}>修改密码</h2>
      <div className="vava-field">
        <label className="vava-label">当前密码</label>
        <input className="vava-input" type="password" value={cur}
               onChange={(e) => setCur(e.target.value)} disabled={saving} />
      </div>
      <div className="vava-field">
        <label className="vava-label">新密码（至少 8 位）</label>
        <input className="vava-input" type="password" value={next}
               onChange={(e) => setNext(e.target.value)} disabled={saving} />
      </div>
      <div className="vava-field">
        <label className="vava-label">确认新密码</label>
        <input className="vava-input" type="password" value={confirm}
               onChange={(e) => setConfirm(e.target.value)} disabled={saving} />
      </div>
      <div style={{ marginTop: 4 }}>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? '修改中…' : '修改密码'}
        </button>
      </div>
      <Notice kind={msg?.kind}>{msg?.text}</Notice>
    </div>
  );
};
